import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { Constants } from '../../helper/constants';
import { DocumentsService } from '../../services/documents.service';
import { QuestionService } from '../../services/question.service';
import { DocumentsModel } from '../../viewmodels/documents.model';
import { QuestionsModel } from '../../viewmodels/questions.model';
import { ReasonModel } from '../../viewmodels/reason.model';
import { Global } from '../../../../app.global';
import { GlobalObservables } from '../../../../app.global.observer';
import { QuestionAndAnswerModel } from '../../viewmodels/questionAndAnswer.model';
import { filterTouchedQuestions } from '../../helper/touched-questions';
import { SubscriptionLike as ISubscription, Observable, forkJoin, of, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, tap } from 'rxjs/operators';
import { QuestionsConst } from '../../helper/questionConst';
import { AdminService } from '../../../../layouts/services/admin.service';
import { AdditionalExplanationViewModel } from '../../../../layouts/viewmodels/additionalExplanationViewModel.model';
import { QuestionProcessModel } from '../../viewmodels/questionProcess.model';
import { StorageService } from '../../services/storage.service';
import { SettingsService } from '../../../../authentication/services/settings.service';

interface EvidenceDocumentGuess {
  documentType: string;
  confidence: number | null;
}

/**
 * Maps each misc_document_schema_v2 enum value (normalized: lowercased, whitespace stripped) to the
 * real RM5 document checklist caption(s) it should badge. A plain string-equality match between the
 * enum wording and a caption doesn't work here - the enum values are written as descriptive prompts
 * for the AI to classify against, not verbatim caption text, and several of them legitimately span
 * more than one existing checklist caption (e.g. the DVLA entry covers 4 separate captions).
 *
 * Entries with an empty array have no corresponding checklist caption yet - the guess still gets
 * logged/shown as evidence text, it just won't badge any specific checklist item until a matching
 * document type exists in RM5's catalog.
 */
const MISC_GUESS_TO_DOCUMENT_CAPTIONS: Record<string, string[]> = {
  'vehiclebreakdown/recoverycompanyreport': ['Breakdown Co Report'],
  'evidencethevehiclewasstolen(insurancetheftclaimorpolicecrimereference)': ['Theft - Proof Of'],
  'insurancewrite-offletter': ['Insurance Doc'],
  'policecustody/detentionrecord': ['Police Document'],
  policeform4737: ['Form 4737'],
  'vehicleparts,removal,orrepairreceipt': ['Parts', 'Removal', 'Repair'],
  // Overlaps with the "nto" root category - a driver's written response to an NTO should usually be
  // classified as appeal_letter/nto at the root level, not reach this guess at all.
  "driver'swrittenresponsetoanoticetoowner": ['NTO'],
  parkingvoucher: ['Voucher'],
  parkingpermitorseasonticket: ['Season Ticket', 'Permit'],
  'pay&displayparkingticket': ['Pay & Display Ticket'],
  councildispensationletter: ['Dispensation'],
  'dvlavehicleregistration/keeperdocument(v5c,taxdisc,ordvlaletter)': [
    'Tax Disc',
    'V5C Document',
    'DVLA Letter',
    'Vehicle Registration Doc',
  ],
  'proofthepcnitselfwasalreadypaid(cheque,card,cash,orpostalorder)': [
    'Cash PCN Payment',
    'Cheque - Copy Of Both Sides',
    'Postal Order',
    'Payment Related',
  ],
  'healthemergencybadge(heb)': ["Health Emerg'y Badge"],
  letterfromapolicesupervisororseniorofficer: ['Police Document'],
  'work/jobsheet-cashcarryingduties': ['Job Sheet - Cash Carrying'],
  'work/jobsheetorletter-patienttransport': ['Job Sheet/Letter - Medical'],
  'privatehirevehicle/driverlicence': ['Private-Hire Licence'],
  'work/jobsheet-statutoryundertakingworks': ['Job Sheet - Stat Undertaking'],
  letterfromaseniormanagerorofficial: ['Letter From Senior', 'Letter From Superior'],
  'work/jobsheet-utilitycompanyworks': ['Job Sheet - Utility'],
  evchargingsessionrecordorreceipt: ['Proof Of Charging Session'],
  personalcircumstancesstatement: [],
  roaduserchargewhitelistexemptionnotification: ['Whitelist Exemption Notification'],
  proofofroaduserchargepayment: ['Proof Of Road User Charge Payment'],
  letterfromabailifforenforcementagent: [],
  'non-medicalemergencystatement': [],
};

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'documents-questions',
  templateUrl: './documents-questions.component.html',
  styleUrls: ['./documents-questions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentsQuestionsComponent implements OnInit, OnDestroy {
  // Debounce (ms) before a multi-select question change triggers backend recalculation - lets a
  // user select/deselect several options before recalculation fires. Board-requested range: 2-3s.
  private static readonly MULTI_SELECT_RECALC_DEBOUNCE_MS = 2500;

  public constants = Constants;

  public documentsList: Array<DocumentsModel> = [];
  public questionsList: Array<QuestionsModel> = [];
  public additionalExplanationIdList: number[] = [];

  public reasonsIdList = [];
  public questionProcess: QuestionProcessModel;
  public loadingState = false;

  /* Additional Explanation */
  public additionalExplanationsMaster: Array<AdditionalExplanationViewModel> = [];
  public additionalExplanationsSelected: number[] = [];

  noDataState = true;
  questionsLoadingState = false;
  getAdditionalExplanationsError = false;
  getQuestionsError = false;
  getDocumentsError = false;
  isLetterChanging = false;
  clarificationQuestions = [];
  prologueQuestions = [];
  onInitGetQuestions = true;
  checkboxQuestionErrorId = 'checkbox-answers-empty-error';
  loadingMessage = 'loading questions';
  isNexusEnabled = false;
  showDocumentConfidence = false;
  spinnerStyle = 'spinner3';
  evidenceDocuments: string[] = [];
  evidenceDocumentGuesses: EvidenceDocumentGuess[] = [];
  evidenceDocumentConfidences: EvidenceDocumentGuess[] = [];

  private loadingQuestionsMessage = 'loading questions';
  private waitingForReasonsMessage = 'please select a reason';
  private reasonsChangingMessage = 'reasons have changed';
  private activeRequests = new Map<string, string>(); // Store request type → request ID
  private isFetchingAllData = false;
  private lastQuestionIdToScrollTo: string;
  private onLetterTextTypeChangedEvent: ISubscription;
  private onLetterTemplateChangedEvent: ISubscription;
  private onLetterChangingEvent: ISubscription;
  private onLetterChangedEvent: ISubscription;
  private onReasonsChangingEvent: ISubscription;
  private onReasonsChangedEvent: ISubscription;
  private onSelectedReasonsUpdatedEvent: ISubscription;
  private onCustomParagraphsShowEvent: ISubscription;
  private removeCustomLetterEvent: ISubscription;
  private removeCustomParagraphEvent: ISubscription;
  private onCustomCustomParagraphsVisibilityChangedEvent: ISubscription;
  private explanationChangeSubject = new Subject<void>();
  private explanationChangeSub: ISubscription;
  private multiSelectRecalcSubject = new Subject<void>();
  private multiSelectRecalcSub: ISubscription;

  constructor(
    private documentsService: DocumentsService,
    private adminService: AdminService,
    public global: Global,
    public globalObs: GlobalObservables,
    public questionServ: QuestionService,
    public questionConst: QuestionsConst,
    private storageService: StorageService,
    private settingsService: SettingsService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  // Getter to dynamically retrieve selected reasons from storage
  get selectedReasons(): Array<ReasonModel> {
    return this.storageService.getReasons(); // Call storage service method
  }

  ngOnInit() {
    this.questionProcess = new QuestionProcessModel();

    if (!this.selectedReasons || this.selectedReasons.length < 1) {
      this.noDataState = true;
      this.loadingMessage = this.waitingForReasonsMessage;
      this.onInitGetQuestions = false;
      this.settingsService.getApplicationSetting('NexusEnabled').subscribe((result) => {
        if (typeof result === 'string') {
          this.isNexusEnabled = result.toLowerCase() === 'true';
        } else {
          this.isNexusEnabled = false;
        }
      });
    } else {
      this.noDataState = false;
      this.loadingMessage = this.loadingQuestionsMessage;
      this.getAllData();
    }

    this.onLetterTextTypeChangedEvent = this.globalObs.onLetterTextTypeChanged.subscribe(() =>
      this.onLetterTextTypeChanged()
    );

    // Was wired to onLetterChanged(), which only sets isLetterChanging = false -
    // so changing the letter template never triggered a refetch and the letter
    // never rebuilt. The dedicated handler below it (onLetterTemplateChanged)
    // was written for this and never called. Compare the letter-text-set
    // selector above, which is wired to its own handler and does work.
    this.onLetterTemplateChangedEvent = this.globalObs.letterTemplateChanged.subscribe(() =>
      this.onLetterTemplateChanged()
    );

    this.onLetterChangingEvent = this.globalObs.onLetterChanging.subscribe(() =>
      this.onLetterChanging()
    );

    this.onLetterChangedEvent = this.globalObs.onLetterChanged.subscribe(() =>
      this.onLetterChanged()
    );

    this.onReasonsChangingEvent = this.globalObs.onReasonsChanging.subscribe(() =>
      this.onReasonsChanging()
    );

    this.onCustomParagraphsShowEvent = this.globalObs.onCustomParagraphsShow.subscribe(
      (visibility: boolean) => this.onCustomParagraphsVisibilityChanged(visibility)
    );

    this.onReasonsChangedEvent = this.globalObs
      .getDebouncedOnReasonsChanged()
      .subscribe(() => this.onReasonsChanged());

    this.removeCustomLetterEvent = this.globalObs.removeCustomLetter.subscribe(() =>
      this.onRemoveCustomLetter()
    );

    this.removeCustomParagraphEvent = this.globalObs.removeCustomParagraph.subscribe(() =>
      this.onRemoveCustomParagraph()
    );

    const storedEvidenceDocs = localStorage.getItem(this.getEvidenceDocsKey());
    this.evidenceDocuments = storedEvidenceDocs ? JSON.parse(storedEvidenceDocs) : [];

    const storedEvidenceDocGuesses = localStorage.getItem(this.getEvidenceDocGuessesKey());
    this.evidenceDocumentGuesses = storedEvidenceDocGuesses
      ? JSON.parse(storedEvidenceDocGuesses)
      : [];

    const storedEvidenceDocConfidences = localStorage.getItem(this.getEvidenceDocConfidencesKey());
    this.evidenceDocumentConfidences = storedEvidenceDocConfidences
      ? JSON.parse(storedEvidenceDocConfidences)
      : [];

    this.settingsService
      .getApplicationSetting('Nexus.ShowDocumentConfidence')
      .subscribe((result) => {
        this.showDocumentConfidence = typeof result === 'string' && result.toLowerCase() === 'true';
      });

    this.settingsService.getApplicationSetting('Council.SpinnerStyle').subscribe({
      next: (result) => {
        this.spinnerStyle = typeof result === 'string' && result ? result : 'spinner3';
        this.markForCheck();
      },
      error: () => {
        this.spinnerStyle = 'spinner3';
        this.markForCheck();
      },
    });

    this.explanationChangeSub = this.explanationChangeSubject
      .pipe(debounceTime(350))
      .subscribe(() => this.questionChanged());

    this.multiSelectRecalcSub = this.multiSelectRecalcSubject
      .pipe(debounceTime(DocumentsQuestionsComponent.MULTI_SELECT_RECALC_DEBOUNCE_MS))
      .subscribe(() => this.getAllData());
  }

  public hasEvidenceType(type: string): boolean {
    const storedDocs = localStorage.getItem(this.getEvidenceDocsKey());
    const evidenceDocs: string[] = storedDocs ? JSON.parse(storedDocs) : [];

    const normalizedType = type.replace(/\s+/g, '').toLowerCase();
    return evidenceDocs.some((doc) => {
      const normalizedDoc = doc.replace(/\s+/g, '').toLowerCase();
      return normalizedDoc === normalizedType;
    });
  }

  public documentHasEvidence(caption: string): boolean {
    if (!caption) {
      return false;
    }

    const storedDocs = localStorage.getItem(this.getEvidenceDocsKey());
    const evidenceDocs: string[] = storedDocs ? JSON.parse(storedDocs) : [];

    const normalizedCaption = caption.replace(/\s+/g, '').toLowerCase();
    return evidenceDocs.some((doc) => {
      const normalizedDoc = doc.replace(/\s+/g, '').toLowerCase();
      return normalizedDoc === normalizedCaption;
    });
  }

  /**
   * An unconfirmed AI guess (from an unclassified/"misc_document" upload) whose type maps to this
   * checklist item's caption - either directly (wording happens to match) or via
   * MISC_GUESS_TO_DOCUMENT_CAPTIONS (the normal case, since the analyser's enum values are
   * descriptive prompts for the AI, not verbatim caption text). Never used to auto-select a
   * checkbox - only to show a distinct "AI guess (confidence%)" badge alongside the existing AI flag.
   */
  public documentGuess(caption: string): EvidenceDocumentGuess | null {
    if (!caption) {
      return null;
    }

    const storedGuesses = localStorage.getItem(this.getEvidenceDocGuessesKey());
    const guesses: EvidenceDocumentGuess[] = storedGuesses ? JSON.parse(storedGuesses) : [];

    const normalize = (s: string) => (s || '').replace(/\s+/g, '').toLowerCase();
    const normalizedCaption = normalize(caption);

    return (
      guesses.find((g) => {
        const normalizedGuessType = normalize(g.documentType);
        if (normalizedGuessType === normalizedCaption) {
          return true;
        }
        const mappedCaptions = MISC_GUESS_TO_DOCUMENT_CAPTIONS[normalizedGuessType];
        return mappedCaptions?.some((c) => normalize(c) === normalizedCaption) ?? false;
      }) ?? null
    );
  }

  /**
   * The AI confidence for a CONFIRMED (root-category) document match, if the
   * Nexus.ShowDocumentConfidence setting is enabled. Purely a display add-on to the existing
   * "AI" badge - the document is already selected/confirmed regardless of this value.
   */
  public documentConfidence(caption: string): EvidenceDocumentGuess | null {
    if (!caption || !this.showDocumentConfidence) {
      return null;
    }

    const storedConfidences = localStorage.getItem(this.getEvidenceDocConfidencesKey());
    const confidences: EvidenceDocumentGuess[] = storedConfidences
      ? JSON.parse(storedConfidences)
      : [];

    const normalizedCaption = caption.replace(/\s+/g, '').toLowerCase();
    return (
      confidences.find(
        (c) => (c.documentType || '').replace(/\s+/g, '').toLowerCase() === normalizedCaption
      ) ?? null
    );
  }

  logQuestionAnswer(question, event) {
    // console.log(`[Component] Answer selected for Question ID: ${question.id} | Answer: ${event.answer}`);
  }

  getAllData() {
    if (!this.global.PCN.pcn) {
      return;
    }

    if (this.isFetchingAllData) {
      // console.log(`[Component] Skipping getAllData() - A request is already in progress.`);
      return;
    }

    this.isFetchingAllData = true;
    // console.log('Fetching question data');
    const requestId = this.trackRequestStart('getAllData');

    forkJoin({
      explanations: this.getAdditionalExplanations(),
      documents: this.getDocuments(),
      questions: this.getQuestionsList(),
    }).subscribe({
      error: (error) => {
        // isFetchingAllData has to be released here too, not only in complete.
        //
        // RxJS calls error or complete, never both. So an error left this flag
        // true for the rest of the component's life, and the guard at the top
        // of getAllData() then returned early on every subsequent call - the
        // user changed an answer, a document or an explanation and nothing
        // happened at all, with no error and no spinner. One failed refresh
        // disabled refreshing.
        this.isFetchingAllData = false;
        this.trackRequestComplete(requestId);
        this.handleError(error);
      },
      complete: () => {
        this.trackRequestComplete(requestId);
        this.isFetchingAllData = false;

        // forkJoin completes successfully even when a stream failed, because
        // each one catches its own error and returns of([]) so the others still
        // run. That is deliberate - a failed document fetch should not hide the
        // questions - but it means "complete" does not mean "worked".
        //
        // Emitting onQuestionsChanged here regardless is what let the letter
        // regenerate from stale answers: processQuestions never ran, so the
        // answers on screen are the previous ones, and the letter would be
        // built from them as though they were current. On a statutory notice
        // that is the wrong failure to choose.
        if (
          this.getQuestionsError ||
          this.getDocumentsError ||
          this.getAdditionalExplanationsError
        ) {
          this.globalObs.onQuestionsFailed.next(null);
          return;
        }
        // console.log('Question data PROCESSED');
        // console.log('Loading State: ' + this.loadingState);
        // console.log('No Data State: ' + this.noDataState);
        // console.log('Reasons: ' + this.selectedReasons.length);

        // emit on questions changed
        this.globalObs.onQuestionsChanged.next(null);
      },
    });
  }

  getAdditionalExplanations(): Observable<any> {
    this.getAdditionalExplanationsError = false;

    if (!this.global.PCN.pcn) {
      // console.log(`[Component] Skipping additional explanations: No valid PCN`);
      return of([]); // Return empty array instead of making an API call
    }

    const reqId = this.trackRequestStart('getAdditionalExplanations');

    return this.adminService.getAdditionalExplanationsForPcn(this.global.PCN.pcn).pipe(
      tap((explanations) => this.processAdditionalExplanations(explanations)), // Process after fetching
      catchError((error) => {
        console.error(`[Component] Error fetching additional explanations`, error);
        this.getAdditionalExplanationsError = true;
        return of([]); // Prevent failure from breaking `forkJoin`
      }),
      finalize(() => {
        // console.log(
        //   `[Component] Additional Explanations: ${this.additionalExplanationList.length}`
        // );
        this.trackRequestComplete(reqId);
      })
    );
  }

  getDocuments(): Observable<any> {
    this.getDocumentsError = false;

    if (!this.global.PCN.pcn) {
      // console.log(`[Component] Skipping getDocuments: No valid PCN`);
      return of([]); // ✅ Return an empty observable to prevent issues in `forkJoin`
    }

    const reqId = this.trackRequestStart('getDocuments');

    return this.documentsService.getDocuments(this.global.PCN.pcn).pipe(
      tap((documents) => this.processDocuments(documents)), // ✅ Process after fetching
      catchError((error) => {
        console.error(`[Component] Error fetching documents`, error);
        this.getDocumentsError = true;
        return of([]); // ✅ Prevent failure from breaking `forkJoin`
      }),
      finalize(() => {
        // console.log(`[Component] Documents loaded: ${this.documentsList.length}`);
        this.trackRequestComplete(reqId);
      })
    );
  }

  getQuestionsList(): Observable<any> {
    this.getQuestionsError = false;

    if (!this.global.PCN.pcn) {
      return of([]); // ✅ Prevent issues in `forkJoin`
    }

    this.globalObs.onQuestionsChanging.next(true);
    const reqId = this.trackRequestStart('getQuestionsList');

    // This hits the AI-free getQuestions endpoint (DisplayQuestionsAndAnswers), which never invokes
    // QuestionAgent - so there's no echo-poisoning risk here, and the full current state is what its
    // own "already answered" continuity checks expect.
    //
    // The live list wins whenever it has anything in it. It is empty on first load, and when a
    // reason, letter text type or letter template change clears it before refetching; sending []
    // then made the server walk its defaults and build the letter from them, while the screen still
    // showed the user's answers. So fall back to the remembered copy, which resetPcn, login and
    // logout all clear, so it never carries another case's answers.
    const liveAnswers = this.getQuestionsAndAnswersModel(this.questionsList);
    this.questionProcess.questionsAndAnswers =
      liveAnswers.length > 0 ? liveAnswers : [...(this.global.questionsAndAnswers ?? [])];
    this.questionProcess.additionalExplanationIds = this.additionalExplanationsSelected;

    return this.questionServ.getQuestions(this.global.PCN.pcn, this.questionProcess).pipe(
      tap((questions) => {
        // console.log('[Component] Processing questions:', questions);
        this.processQuestions(questions);
      }),
      catchError((error) => {
        // The flag and the message that renders it both already existed;
        // nothing set it. So a failed question fetch was completely silent:
        // processQuestions never ran, the list on screen stayed as it was, and
        // the user had no way to know the screen was out of date. Documents and
        // additional explanations both set their equivalent flag - this one was
        // simply missed.
        console.error('[Component] Error fetching questions', error);
        this.getQuestionsError = true;

        return of([]);
      }),
      finalize(() => {
        // console.log('[Component] Questions request completed.');
        this.trackRequestComplete(reqId);
      })
    );
  }

  toggleExplanation(explanation: AdditionalExplanationViewModel) {
    explanation.expanded = !explanation.expanded;
  }

  setSelectedAdditionalExplanations() {
    this.additionalExplanationsMaster.forEach((exp) => {
      if (this.additionalExplanationsSelected.indexOf(exp.id) > -1) {
        exp.selected = true;
      }
    });
    // console.log(
    //   `[Component] Master additional explanations: ${this.additionalExplanationsMaster.length}`
    // );
    // this.cdr.markForCheck();
  }

  onExplanationSelected(explanation) {
    this.globalObs.beforeAdditionalExplanationSaved.next(true);
    if (explanation.selected && !this.additionalExplanationsSelected.includes(explanation.id)) {
      this.additionalExplanationsSelected.push(explanation.id);
    } else {
      const index = this.additionalExplanationsSelected.indexOf(explanation.id);
      if (index !== -1) {
        this.additionalExplanationsSelected.splice(index, 1);
      }
    }

    this.onAdditionalExplanationsChanged();
  }

  setSelectedDocuments() {
    this.documentsService.getSelectedDocuments(this.global.PCN.pcn).subscribe((res) => {
      const normalize = (s: any) => String(s).toLowerCase().replace(/\s+/g, '');
      const selectedDocIdList = res.map((d) => String(d.id));

      const storedEvidenceDocs = localStorage.getItem(this.getEvidenceDocsKey());
      const storedEvidenceDocNames: string[] = storedEvidenceDocs
        ? JSON.parse(storedEvidenceDocs).map(normalize)
        : [];

      // Documents from getDocuments() never carry selection state (DocumentsModel.selected starts
      // falsy on every fresh fetch) - selectedDocIdList, from the DB via getSelectedDocuments(), is
      // the only reliable signal for "already applied". Using doc.selected to detect a "just
      // selected" transition would re-trigger on every single refresh, not just the first time.
      const aiDocsToApply: DocumentsModel[] = [];

      this.documentsList.forEach((doc) => {
        const normalizedName = normalize(doc.caption || '');
        const alreadySelected = selectedDocIdList.includes(String(doc.id));
        const evidenceMatch = storedEvidenceDocNames.includes(normalizedName);

        doc.selected = alreadySelected || evidenceMatch;

        if (!alreadySelected && evidenceMatch && this.documentHasEvidence(doc.caption)) {
          aiDocsToApply.push(doc);
        }
      });

      // OnPush nudge
      this.documentsList = [...this.documentsList];
      this.cdr.markForCheck();

      if (aiDocsToApply.length > 0) {
        this.globalObs.onDocumentsChanging.next(true);
        forkJoin(
          aiDocsToApply.map((doc) =>
            this.documentsService.addSelectedDocument(doc.id, this.global.PCN.pcn)
          )
        ).subscribe({
          next: (results) => {
            if (results.some((r) => r)) {
              this.getAllData();
            }
          },
          error: () => {},
        });
      }
    });
  }

  setClarificationQuestions() {
    this.clarificationQuestions = [];
    this.questionsList.forEach((question) => {
      if (this.isQuestionType(this.questionConst.clarificationType, question.questionType)) {
        this.clarificationQuestions.push(question);
      }
    });
  }

  setPrologueQuestions() {
    this.prologueQuestions = [];
    this.questionsList.forEach((question) => {
      if (this.isQuestionType(this.questionConst.prologueType, question.questionType)) {
        this.prologueQuestions.push(question);
      }
    });
  }

  mergeGlobalRequestQuestions(result) {
    const globalQuestionsId = [];
    this.global.questionsAndAnswers.forEach((globalQuestion) => {
      globalQuestionsId.push(globalQuestion.questionId);
    });
    result.forEach((question) => {
      const globalIndex = globalQuestionsId.indexOf(question.id);
      if (globalIndex > -1) {
        const globalQuestion = this.global.questionsAndAnswers[globalIndex];
        question.defaultAnswer = globalQuestion.answer.value;
        // getAllData()'s AI-free refresh (getQuestionsList) recomputes every question fresh from the
        // server and carries no AI badge info of its own - without this, the badge set by
        // handleReasonAndFetchQuestions's getAIAnsweredQuestions response gets dropped on the very
        // next refresh (e.g. from clicking a radio button), even though defaultAnswer survives.
        if (globalQuestion.recommendedAnswerId != null) {
          question.recommendedAnswerId = globalQuestion.recommendedAnswerId;
        }
      }
    });
    this.questionsList = result;
  }

  getQuestionsByReason(reasonId) {
    if (this.reasonsIdList.indexOf(reasonId) === -1) {
      return [];
    }
    const questionsList = [];
    this.questionsList.forEach((question) => {
      if (
        this.isQuestionType(this.questionConst.noneType, question.questionType) &&
        question.reasonId === reasonId
      ) {
        questionsList.push(question);
      }
    });
    return questionsList;
  }

  getQuestionsByDocument(documentId) {
    const questionsList = [];
    this.questionsList.forEach((question) => {
      if (
        this.isQuestionType(this.questionConst.noneType, question.questionType) &&
        question.reasonId === documentId
      ) {
        questionsList.push(question);
      }
    });
    return questionsList;
  }

  getUnsortedQuestions() {
    const questionsList = [];
    const documentsIds = [];
    const reasonsIds = [];
    this.documentsList.forEach((document) => documentsIds.push(document.id));
    this.selectedReasons.forEach((reason) => reasonsIds.push(reason.id));
    this.questionsList.forEach((question) => {
      if (this.isQuestionType(this.questionConst.noneType, question.questionType)) {
        if (
          documentsIds.indexOf(question.reasonId) === -1 &&
          reasonsIds.indexOf(question.reasonId) === -1
        ) {
          questionsList.push(question);
        }
      }
    });
    return questionsList;
  }

  onDocumentSelected(document) {
    this.globalObs.onDocumentsChanging.next(true);
    this.questionsLoadingState = true;
    // this.disableQuestions(true);
    if (document.selected) {
      this.documentsService
        .addSelectedDocument(document.id, this.global.PCN.pcn)
        .subscribe({
          next: (result) => {
            if (result) {
              // console.log('Calling getQuestionsList from onDocumentSelected');
              // this.getQuestionsList();
              this.getAllData();
            }
          },
          error: () => {},
          complete: () => {
            this.globalObs.onDocumentsChanged.next(true);
            setTimeout(() => {
              this.questionsLoadingState = false;
            }, 700);
          },
        })
        .add(() => {
          // this.disableQuestions(false);
        });
    } else {
      this.documentsService
        .removeSelectedDocument(document.id, this.global.PCN.pcn)
        .subscribe({
          next: (result) => {
            if (result) {
              localStorage.removeItem(this.getEvidenceDocsKey());
              // console.log('Calling getQuestionsList from onDocumentSelected');
              // this.getQuestionsList();
              this.getAllData();
            }
          },
          error: () => {},
          complete: () => {
            this.globalObs.onDocumentsChanged.next(true);
            setTimeout(() => {
              this.questionsLoadingState = false;
            }, 700);
          },
        })
        .add(() => {
          // this.disableQuestions(false);
        });
    }
  }

  onLetterTextTypeChanged() {
    if (this.selectedReasons.length > 0) {
      this.additionalExplanationsSelected = [];
      this.documentsList = [];
      this.questionsList = [];
      // console.log('Calling getAllData from onLetterTextTypeChanged');
      this.getAllData();
    }
  }

  onLetterTemplateChanged() {
    if (this.selectedReasons.length > 0) {
      this.additionalExplanationsSelected = [];
      this.documentsList = [];
      this.questionsList = [];
      // console.log('Calling getAllData from onLetterTemplateChanged');
      this.getAllData();
    }
  }

  onLetterChanging() {
    // disable the question block
    this.isLetterChanging = true;
  }

  onLetterChanged() {
    this.isLetterChanging = false;
  }

  onReasonsChanging() {
    // console.log('[Component] [documents-questions] Reasons changing');
    // this.loadingMessage = this.reasonsChangingMessage;
    this.showSpinner(this.reasonsChangingMessage);
  }

  onReasonsChanged() {
    // console.log('[Component] [documents-questions] Reasons changed');
    if (this.selectedReasons.length > 0) {
      // console.log('Reason(s) selected');

      this.noDataState = false;
      this.loadingMessage = this.loadingQuestionsMessage;
      this.additionalExplanationsSelected = [];
      // this.documentsList = [];
      this.questionsList = [];
      this.getAllData();
    } else {
      this.loadingMessage = this.waitingForReasonsMessage;
      this.noDataState = true;
    }
    // Force change detection
    this.markForCheck();
  }

  onRemoveCustomLetter() {
    this.getQuestionsList();
  }

  onRemoveCustomParagraph() {
    this.getQuestionsList();
  }

  onCustomParagraphsVisibilityChanged(visibility: boolean) {
    // console.log('[Documents-Questions Component] Custom paragraphs visibility changed:', visibility);
    // if (!visibility) {
    //   // this.loadingMessage = this.loadingQuestionsMessage;
    //   this.showSpinner(this.loadingQuestionsMessage);
    //   this.getQuestionsList();
    //   // console.log('Reasons: ', this.selectedReasons.length);
    //   // console.log('Questions: ', this.questionsList.length);
    //   // console.log('Questions Loading State: ', this.questionsLoadingState);
    // }
  }

  /**
   * Full current answer state (AI-picked or human-picked) for every question - used for letter
   * generation and final-save audit logging, both of which need to know what's actually currently
   * selected regardless of who/what picked it. NOT what should be echoed back to the AI-answering
   * endpoint as "already answered" - see getTouchedQuestionsAndAnswersModel for that.
   */
  getQuestionsAndAnswersModel(questionsList) {
    const questionsAndAnswersList: Array<QuestionAndAnswerModel> = [];

    questionsList.forEach((question) => {
      questionsAndAnswersList.push({
        questionId: question.id,
        recommendedAnswerId: question.recommendedAnswerId,
        answer: {
          questionClass: question.questionClass,
          value: question.answerSelected,
        },
      });
    });

    return questionsAndAnswersList;
  }

  /**
   * Subset of getQuestionsAndAnswersModel containing only questions the user has genuinely
   * clicked/changed themselves - what gets reported to NexusAudit.UserQuestionIds. Sending every
   * question (as getQuestionsAndAnswersModel does) would record AI-only-suggested answers as if a
   * human had confirmed them.
   */
  getTouchedQuestionsAndAnswersModel(questionsList): Array<QuestionAndAnswerModel> {
    return filterTouchedQuestions(
      this.getQuestionsAndAnswersModel(questionsList),
      this.global.touchedQuestionIds
    );
  }

  questionChanged(question?) {
    // console.log('Question Changed Triggered');
    if (question?.id != null) {
      // Questions the user has genuinely clicked/changed themselves, as opposed to ones merely
      // rendered with an AI-suggested default. Reported incrementally to NexusAudit.UserQuestionIds
      // so the server has an up-to-date record of real human answers to consult the next time
      // QuestionAgent runs (a second document upload, say) - without waiting for final save. The set
      // lives on Global so the final save reports the same questions.
      this.global.touchedQuestionIds.add(question.id);
      this.postIncrementalQuestionAudit();
    }

    if (this.areCompulsaryQuestionsUnselected()) {
      // console.log('Unaswered questions: Scrolling to error');
      document.getElementById(this.checkboxQuestionErrorId).scrollIntoView({
        behavior: 'smooth', // Optional: Makes the scroll smooth
        block: 'nearest', // Optional: Scrolls only if the element is outside the viewport
        inline: 'nearest', // Prevents horizontal scrolling
      });
      return;
    }

    // Store the element we want to scroll back to
    this.lastQuestionIdToScrollTo = `question-${question?.id}`;

    this.global.questionsAndAnswers = this.getQuestionsAndAnswersModel(this.questionsList);
    this.questionServ.setLocalQuestionsAndAnswers(
      this.getQuestionsAndAnswersModel(this.questionsList)
    );

    // Multi-select changes are debounced so a user can select/deselect several options before
    // recalculation fires - all other question types keep the existing immediate behaviour.
    if (
      question &&
      this.isQuestionClass(this.questionConst.multipleAnswerClass, question.questionClass)
    ) {
      this.multiSelectRecalcSubject.next();
    } else {
      this.getAllData();
    }
  }

  onAdditionalExplanationsChanged() {
    this.explanationChangeSubject.next();
  }

  areCompulsaryQuestionsUnselected() {
    let bool = false;
    this.questionsList.forEach((question) => {
      if (
        this.isQuestionClass(this.questionConst.multipleAnswerClass, question.questionClass) &&
        question.answerSelected.length === 0 &&
        question.isCompulsary
      ) {
        bool = true;
      }
    });
    return bool;
  }

  getParseInt(value) {
    return parseInt(value.toString(), 10);
  }

  isQuestionType(type, typeId) {
    return this.global.questionTypes[typeId] === type;
  }

  isQuestionClass(questionClass, classId) {
    return this.global.questionClasses[classId] === questionClass;
  }

  /**
   * TrackBy function for questions. When using *ngFor, this function helps Angular
   * optimize rendering by tracking items by their unique ID.
   */
  trackByQuestionId(_index: number, question: QuestionsModel): number {
    return question.id;
  }

  /**
   * TrackBy function for documents. When using *ngFor, this function helps Angular
   * optimize rendering by tracking items by their unique ID.
   */
  trackByDocumentId(_index: number, document: DocumentsModel): number {
    return document.id;
  }

  /**
   * TrackBy function for Reasons. When using *ngFor, this function helps Angular
   * optimize rendering by tracking items by their unique ID.
   */
  trackByReasonId(_index: number, reason: ReasonModel): number {
    return reason.id;
  }

  trackByExplanationId(_index: number, explanation: AdditionalExplanationViewModel): number {
    return explanation.id;
  }

  ngOnDestroy() {
    this.onSelectedReasonsUpdatedEvent?.unsubscribe();
    this.onLetterTextTypeChangedEvent?.unsubscribe();
    this.onLetterTemplateChangedEvent?.unsubscribe();
    this.onLetterChangingEvent?.unsubscribe();
    this.onLetterChangedEvent?.unsubscribe();
    this.onReasonsChangingEvent?.unsubscribe();
    this.onReasonsChangedEvent?.unsubscribe();
    this.removeCustomLetterEvent?.unsubscribe();
    this.removeCustomParagraphEvent?.unsubscribe();
    this.explanationChangeSub?.unsubscribe();
    this.multiSelectRecalcSub?.unsubscribe();
  }

  private showSpinner(message: string) {
    if (!this.loadingState) {
      if (message !== undefined) {
        this.loadingMessage = message;
      }
      this.loadingState = true;
      // console.log('Show Spinner; loading state = ' + this.loadingState);
      this.markForCheck(); // Ensure change detection runs
    }
  }

  private hideSpinner() {
    if (this.loadingState) {
      this.loadingState = false;
      // console.log('Hide Spinner; loading state = ' + this.loadingState);
      this.markForCheck(); // Ensure change detection runs

      if (this.lastQuestionIdToScrollTo) {
        setTimeout(() => {
          const el = document.querySelector(`[data-id="${this.lastQuestionIdToScrollTo}"]`);
          if (el) {
            el.scrollIntoView({
              behavior: 'auto',
              block: 'nearest', // <== prevents scrolling beyond bounds
              inline: 'nearest',
            });
          }
          this.lastQuestionIdToScrollTo = null;
        }, 0);
      }
    }
  }

  private processAdditionalExplanations(explanations: any[]) {
    // console.log(`[Component] Processing additional explanations:`, explanations);
    // this.additionalExplanationsSelected = [];
    this.additionalExplanationsMaster = explanations.map((explanation) => ({
      ...explanation,
      expanded: false, // Ensure each explanation starts collapsed
    }));

    this.setSelectedAdditionalExplanations(); // Ensure selected explanations are still set
  }

  private processDocuments(documents: any[]) {
    this.documentsList = documents || [];
    this.setSelectedDocuments(); // Ensure selected documents are correctly applied
  }

  private processQuestions(result: any) {
    if (!result || !result.questions || !result.questions.length) {
      // console.log(`[Component] No questions received.`);
      return;
    }

    this.globalObs.onDecisionSetAction.next(result.decisionAction.toString());

    if (this.global.questionsAndAnswers.length < 1) {
      this.questionsList = result.questions;
    } else {
      this.mergeGlobalRequestQuestions(result.questions);
    }

    this.reasonsIdList = [];
    this.questionsList.forEach((question) => {
      if (this.isQuestionClass(this.questionConst.multipleAnswerClass, question.questionClass)) {
        question.answerSelected = question.defaultAnswer ?? [];
      } else if (
        this.isQuestionClass(this.questionConst.freeTextClass, question.questionClass) ||
        this.isQuestionClass(this.questionConst.freeTextNoValidationClass, question.questionClass)
      ) {
        question.answerSelected = question.defaultAnswer ?? '';
      } else if (
        this.isQuestionClass(this.questionConst.yesNoClass, question.questionClass) ||
        this.isQuestionClass(this.questionConst.multipleChoiceClass, question.questionClass)
      ) {
        question.answerSelected = question.defaultAnswer ?? question.answers[0]?.id;
      } else {
        question.answerSelected = question.defaultAnswer;
      }

      if (!this.reasonsIdList.includes(question.reasonId)) {
        this.reasonsIdList.push(question.reasonId);
      }
    });

    this.setClarificationQuestions();
    this.setPrologueQuestions();
    this.global.questionsAndAnswers = this.getQuestionsAndAnswersModel(this.questionsList);
    this.questionServ.setLocalQuestionsAndAnswers(
      this.getQuestionsAndAnswersModel(this.questionsList)
    );
    this.global.questionsObjectList = this.questionsList;
  }

  private trackRequestStart(requestName: string): string {
    // console.log('Show Spinner (trackRequestStart)');
    this.showSpinner(this.loadingQuestionsMessage); // Show spinner when a request starts
    const requestId = crypto.randomUUID(); // Generate unique ID
    this.activeRequests.set(requestId, requestName); // Track request by ID, not name

    // console.log(`[Component] Tracking request: ${requestName} with ID: ${requestId}`);
    // console.log(`[Component] Active requests:`, Array.from(this.activeRequests.entries()));

    return requestId;
  }

  private trackRequestComplete(requestId: string) {
    if (!this.activeRequests.has(requestId)) {
      return;
    }

    // Remove the request from tracking
    this.activeRequests.delete(requestId);
    // console.log(`[Component] Request completed: ${requestId}`);
    // console.log(
    //   `[Component] Active requests after completion:`,
    //   Array.from(this.activeRequests.entries())
    // );

    // Hide the spinner only if all requests are finished
    if (this.activeRequests.size === 0) {
      setTimeout(() => {
        if (this.activeRequests.size === 0) {
          // console.log('[Component] Force-hiding spinner after delay.');
          // console.log('Hide Spinner (trackRequestComplete)');
          this.hideSpinner();
          this.cdr.markForCheck(); // Trigger change detection
        }
      }, 2000);
    }
  }

  private handleError(error: any) {
    console.error(`[Component] Error: `, error);
    this.getDocumentsError = true;
    this.hideSpinner();
  }

  private markForCheck() {
    // Trigger change detection manually if needed
    this.cdr.markForCheck();
  }

  private getEvidenceDocsKey(): string {
    return `evidenceDocuments_${this.global?.PCN?.pcn ?? 'unknown'}`;
  }

  private getEvidenceDocGuessesKey(): string {
    return `evidenceDocumentGuesses_${this.global?.PCN?.pcn ?? 'unknown'}`;
  }

  private getEvidenceDocConfidencesKey(): string {
    return `evidenceDocumentConfidences_${this.global?.PCN?.pcn ?? 'unknown'}`;
  }

  /**
   * Persists touched-only answers to NexusAudit.UserQuestionIds as they happen, rather than only at
   * final save (nexus-upload.component.ts's handleSave/postSessionAudit) - COALESCE on the server side
   * means omitting updatedReasons/updatedDocuments here leaves those columns untouched.
   */
  private postIncrementalQuestionAudit(): void {
    const pcn = this.global?.PCN?.pcn;
    const sessionGuid = pcn ? localStorage.getItem(`sessionGuid_${pcn}`) : null;
    const rmAccessToken = localStorage.getItem('rm_access_token');
    if (!sessionGuid || !rmAccessToken) {
      return;
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${rmAccessToken}`,
      'Content-Type': 'application/json',
    });
    const params = new HttpParams().set('sessionGuid', sessionGuid);
    const body = {
      questionsAndAnswers: this.getTouchedQuestionsAndAnswersModel(this.questionsList),
      additionalExplanationIds: [] as number[],
    };

    const url = `${environment.apiPath}api/nexus/updateSessionAudit`;
    this.http.post(url, body, { headers, params }).subscribe({ error: () => {} });
  }
}
